import React, { useState } from 'react';
import {
  Badge, Box, Button, Collapse, Flex, IconButton, Stack, Text, Tooltip, useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiEdit2, FiEye, FiPlus, FiServer, FiShoppingCart, FiTrash2,
} from 'react-icons/fi';
import GlassCard from './GlassCard';
import AuroraBackground from './dashboard/AuroraBackground';
import PageHeader from './PageHeader';
import ServerSpecEditorModal from './ServerSpecEditorModal';
import ServerSpecConvertModal from './ServerSpecConvertModal';
import useServerSpecs, { ServerSpecRow, useServerSpecActions } from '../hooks/useServerSpecs';
import { fromSpecJson, nodeSummary } from '../utils/serverSpecCatalog';
import { openPdf, previewPdf } from '../utils/openPdf';
import apiErrorMessage from '../utils/apiError';

// item 1595 (Chad) — the project's Tier 3 SERVER SPECS: vendor-neutral hardware specs for an
// enterprise HA cluster (3/5/7 nodes), each exported as an RFQ PDF to send manufacturers for
// pricing. Lives in the Project Management bucket; touches no money anywhere. Pulse super-admin
// only during rollout (the caller gates rendering, the API gates every call).
const ProjectServerSpecsSection: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, collapse }) => {
  const { data: specs } = useServerSpecs(projectId, true);
  const { archive } = useServerSpecActions(projectId);
  const toast = useToast();
  const [editing, setEditing] = useState<ServerSpecRow | null>(null);
  const [open, setOpen] = useState(false);
  const [converting, setConverting] = useState<ServerSpecRow | null>(null);
  const collapsed = collapse?.collapsed ?? false;
  const rows = specs ?? [];

  const fail = (title: string) => (e: unknown) => toast({
    title, description: apiErrorMessage(e, 'Try again.'), status: 'error', duration: 7000, isClosable: true,
  });

  const onArchive = async (s: ServerSpecRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the server spec "${s.name}"? It's archived, not destroyed.`)) return;
    try { await archive.mutateAsync(s.id); } catch (e) { fail('Could not remove the spec')(e); }
  };

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={collapsed ? 1 : { base: 2, md: 3 }}
      pb={collapsed ? 1 : { base: 2, md: 3 }}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={collapsed}
          icon={<FiServer />}
          title="Server Specs"
          subtitle={`Tier 3 · ${rows.length} ${rows.length === 1 ? 'spec' : 'specs'}`}
        />
        <Collapse in={!collapsed} animateOpacity>
          <GlassCard p={4}>
            {rows.length === 0 && (
              <Text fontSize="sm" opacity={0.65} mb={3}>
                No server specs yet. Design a Tier 3 cluster (3, 5 or 7 identical nodes) and export a vendor-neutral PDF to get pricing from manufacturers.
              </Text>
            )}
            <Stack spacing={2}>
              {rows.map((s) => {
                const doc = fromSpecJson(s.specJson);
                return (
                  <Flex key={s.id} align="center" gap={3} py={1.5} role="group" wrap="wrap">
                    <Box minW={0} flex={1}>
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>{s.name}</Text>
                        <Badge colorScheme="purple">{`${s.nodeCount} nodes`}</Badge>
                      </Flex>
                      <Text fontSize="xs" opacity={0.7}>{`Each node: ${nodeSummary(doc.node)}`}</Text>
                      {doc.conversions.map((c) => (
                        <Text key={c.workOrderId} fontSize="xs">
                          {'→ '}
                          <Text as={RouterLink} to={`/WorkOrder/${c.workOrderId}`} textDecoration="underline" fontWeight="semibold">
                            {c.number || 'Sales order'}
                          </Text>
                        </Text>
                      ))}
                    </Box>
                    <Tooltip label="Preview the RFQ PDF">
                      <IconButton
                        aria-label={`Preview ${s.name} PDF`}
                        icon={<FiEye />}
                        size="sm"
                        variant="ghost"
                        onClick={() => previewPdf(`ServerSpecs/${s.id}/pdf`).catch(fail('Could not build the PDF'))}
                      />
                    </Tooltip>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPdf(`ServerSpecs/${s.id}/pdf`).catch(fail('Could not build the PDF'))}
                    >
                      PDF
                    </Button>
                    <Tooltip label="Convert to a sales order (vendor pricing typed in)">
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiShoppingCart />}
                        onClick={() => setConverting(s)}
                      >
                        Sales order
                      </Button>
                    </Tooltip>
                    <Tooltip label="Edit">
                      <IconButton
                        aria-label={`Edit ${s.name}`}
                        icon={<FiEdit2 />}
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditing(s); setOpen(true); }}
                      />
                    </Tooltip>
                    <IconButton
                      aria-label={`Remove ${s.name}`}
                      icon={<FiTrash2 />}
                      size="xs"
                      variant="ghost"
                      opacity={0.4}
                      _groupHover={{ opacity: 1 }}
                      onClick={() => onArchive(s)}
                    />
                  </Flex>
                );
              })}
            </Stack>
            <Button mt={3} size="sm" leftIcon={<FiPlus />} onClick={() => { setEditing(null); setOpen(true); }}>
              New server spec
            </Button>
          </GlassCard>
        </Collapse>
      </Box>
      <ServerSpecEditorModal projectId={projectId} isOpen={open} onClose={() => setOpen(false)} existing={editing} />
      <ServerSpecConvertModal projectId={projectId} spec={converting} onClose={() => setConverting(null)} />
    </Box>
  );
};

ProjectServerSpecsSection.defaultProps = { collapse: undefined };

export default ProjectServerSpecsSection;
